import { ReportSortColumn, SortDirection } from '../../../shared/types';

export interface SortConfig {
  column: ReportSortColumn;
  direction: SortDirection;
}

interface SortableTableHeaderProps {
  column: ReportSortColumn;
  label: string;
  currentSort: SortConfig | null;
  onSort: (column: ReportSortColumn) => void;
  sortable?: boolean;
  className?: string;
}

/**
 * A table header component that supports sorting.
 * Click to cycle through: none -> desc -> asc -> none
 * Shows arrow indicators for current sort state.
 */
export default function SortableTableHeader({
  column,
  label,
  currentSort,
  onSort,
  sortable = true,
  className = '',
}: SortableTableHeaderProps) {
  const isActive = currentSort?.column === column;
  const direction = isActive ? currentSort.direction : null;

  const handleClick = () => {
    if (!sortable) return;
    onSort(column);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!sortable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSort(column);
    }
  };

  // Arrow icons
  const UpArrow = ({ active }: { active: boolean }) => (
    <svg
      className={`w-3 h-3 ${active ? 'text-blue-600' : 'text-gray-300'}`}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 6.414l-3.293 3.293a1 1 0 01-1.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );

  const DownArrow = ({ active }: { active: boolean }) => (
    <svg
      className={`w-3 h-3 ${active ? 'text-blue-600' : 'text-gray-300'}`}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 13.586l3.293-3.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );

  return (
    <th
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
        sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
      } ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={sortable ? 0 : undefined}
      aria-sort={
        isActive
          ? direction === 'asc'
            ? 'ascending'
            : 'descending'
          : undefined
      }
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {sortable && (
          <div className="flex flex-col -space-y-1">
            <UpArrow active={direction === 'asc'} />
            <DownArrow active={direction === 'desc'} />
          </div>
        )}
      </div>
    </th>
  );
}
